import { useCallback, useEffect, useRef } from 'react';

interface SidebarHandleProps {
  side: 'left' | 'right';
  width: number;
  setWidth: (width: number) => void;
  min: number;
  max: number;
  ariaLabel: string;
}

/**
 * 4 px-wide drag handle for sidebar resize. The pointer down/move/up
 * pattern matches VS Code: pointer is captured on mousedown, x deltas
 * adjust the width directly, double-click resets to the default width
 * via `setWidth(min + (max - min) / 3)`.
 */
export function SidebarHandle({
  side,
  width,
  setWidth,
  min,
  max,
  ariaLabel,
}: SidebarHandleProps) {
  const startRef = useRef<{ startX: number; startWidth: number } | null>(null);

  const onPointerDown = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      e.preventDefault();
      const target = e.currentTarget;
      target.setPointerCapture(e.pointerId);
      startRef.current = { startX: e.clientX, startWidth: width };
      target.style.background = 'rgb(96 165 250 / 0.4)';
    },
    [width],
  );

  const onPointerMove = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      if (!startRef.current) return;
      const delta = e.clientX - startRef.current.startX;
      const next = startRef.current.startWidth + (side === 'left' ? delta : -delta);
      const clamped = Math.max(min, Math.min(max, next));
      setWidth(clamped);
    },
    [min, max, setWidth, side],
  );

  const onPointerUp = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      const target = e.currentTarget;
      target.releasePointerCapture(e.pointerId);
      startRef.current = null;
      target.style.background = '';
    },
    [],
  );

  // Reset to "default" on double-click (a third of the way between min and max).
  const onDoubleClick = useCallback(() => {
    setWidth(Math.round(min + (max - min) / 3));
  }, [min, max, setWidth]);

  // Defensive: clear capture on unmount in case pointer never lifted.
  useEffect(() => {
    return () => {
      startRef.current = null;
    };
  }, []);

  return (
    <div
      role="separator"
      aria-orientation="vertical"
      aria-label={ariaLabel}
      tabIndex={0}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onDoubleClick={onDoubleClick}
      className={[
        'w-1 cursor-col-resize select-none transition-colors',
        'hover:bg-blue-400/40 focus-visible:bg-blue-400/40 focus-visible:outline-none',
      ].join(' ')}
    />
  );
}
